// ***************************************
// tgfFunctions
//
// This namespace object contains a number of useful functions used inside
// the framework and can also be used by the games
//
// Copyright 2015 Tingly Games B.V.
// Last Changed: 2-11-2015
// ***************************************
import { tbAudio } from "../engine/tbAudio";
import { tbGame } from "../engine/tbGame";
import { tbLoader } from "../engine/tbLoader";
import { defaultRandom } from "../engine/tbRandom";
var tgfFunctions = /** @class */ (function () {
    function tgfFunctions() {
    }
    // ***************************************
    // Loading
    // ***************************************
    // Loading the resources with the given settings
    tgfFunctions.loadStart = function (settings) {
        "use strict";
        var i;
        tgfFunctions._loadSettings = settings;
        tgfFunctions._loadTime = tbGame.gameTime;
        for (i = 0; i < tgfFunctions._loadSettings.moments.length; i += 1) {
            tbLoader.startLoading(tgfFunctions._loadSettings.moments[i]);
        }
    };
    // Check whether the loading is complete
    tgfFunctions.loadComplete = function () {
        "use strict";
        var i, ready = true;
        // Check whether the minimum time has passed
        if (tbGame.gameTime < tgfFunctions._loadTime + 1000 * tgfFunctions._loadSettings.minTime) {
            return false;
        }
        // Check whether the required ones are ready
        for (i = 0; i < tgfFunctions._loadSettings.required.length; i += 1) {
            if (!tbLoader.complete(tgfFunctions._loadSettings.required[i])) {
                return false;
            }
        }
        // Check whether also the non-required are ready
        for (i = 0; i < tgfFunctions._loadSettings.moments.length; i += 1) {
            if (!tbLoader.complete(tgfFunctions._loadSettings.moments[i])) {
                ready = false;
            }
        }
        return ready || tbGame.gameTime >= tgfFunctions._loadTime + 1000 * tgfFunctions._loadSettings.maxTime;
    };
    // Return the percentage of loading
    tgfFunctions.loadPercentage = function () {
        "use strict";
        var i, n = 1, perc = 0, mt;
        for (var j = 0; j < tgfFunctions._loadSettings.required.length; j++) {
            if (tgfFunctions._loadSettings.required[j] === "spine") {
                tbLoader.checkSpineLoad();
            }
        }
        if (tgfFunctions.loadComplete()) {
            return 100;
        }
        for (i = 0; i < tgfFunctions._loadSettings.moments.length; i += 1) {
            perc += tbLoader.percentage(tgfFunctions._loadSettings.moments[i]);
            n += 1;
        }
        // Half is determined by the loading and the other half by the time passed
        perc /= n;
        mt = 1000 * Math.max(tgfFunctions._loadSettings.maxTime, tgfFunctions._loadSettings.minTime);
        return perc * Math.min(1, (tbGame.gameTime - tgfFunctions._loadTime) / mt);
    };
    // ***************************************
    // Pausing and resuming everything
    // ***************************************
    // Pause everything
    tgfFunctions.pauseAll = function () {
        "use strict";
        tbAudio.pause("master");
        tbGame.pause();
    };
    // Resume everything (also clear events unless true is passed as parameter)
    tgfFunctions.resumeAll = function (keepEvents) {
        "use strict";
        tbAudio.resume("master");
        if (!keepEvents) {
            tbGame.clearClickEvents();
            tbGame.clearTouchEvents();
            tbGame.clearKeyboardEvents();
        }
        tbGame.resume();
    };
    // Returns whether the time has passed
    tgfFunctions.checkTimeLock = function (timeLock) {
        "use strict";
        if (timeLock === undefined) {
            return true;
        }
        return new Date().getTime() <= timeLock;
    };
    // ***************************************
    // Dealing with objects
    // ***************************************
    // Checks whether the two values are equal (also works for objects, arrays, etc.)
    tgfFunctions.equalValues = function (value1, value2) {
        "use strict";
        var i, obj;
        // Check easy case
        if (value1 === undefined) {
            return value2 === undefined;
        }
        if (value1 === null) {
            return value2 === null;
        }
        if (value2 === undefined) {
            return false;
        }
        if (value2 === null) {
            return false;
        }
        // Check for arrays
        if (Object.prototype.toString.call(value1) === "[object Array]") {
            if (Object.prototype.toString.call(value2) !== "[object Array]") {
                return false;
            }
            if (value1.length !== value2.length) {
                return false;
            }
            for (i = 0; i < value1.length; i += 1) {
                if (!tgfFunctions.equalValues(value1[i], value2[i])) {
                    return false;
                }
            }
            return true;
        }
        // Check for objects
        if (typeof value1 === "object" && value1.constructor === Object) {
            if (typeof value2 !== "object" || value2.constructor !== Object) {
                return false;
            }
            for (obj in value1) {
                if (value1.hasOwnProperty(obj)) {
                    if (!tgfFunctions.equalValues(value1[obj], value2[obj])) {
                        return false;
                    }
                }
            }
            for (obj in value2) {
                if (value2.hasOwnProperty(obj)) {
                    if (!tgfFunctions.equalValues(value1[obj], value2[obj])) {
                        return false;
                    }
                }
            }
            return true;
        }
        // Check for simple types
        return value2 === value1;
    };
    // Create a deep copy of a value (also works for objects, arrays, etc.)
    tgfFunctions.copyValue = function (value) {
        "use strict";
        var i, obj, result;
        // Check easy case
        if (value === undefined) {
            return undefined;
        }
        if (value === null) {
            return null;
        }
        // Check for arrays
        if (Object.prototype.toString.call(value) === "[object Array]") {
            result = [];
            for (i = 0; i < value.length; i += 1) {
                result.push(tgfFunctions.copyValue(value[i]));
            }
            return result;
        }
        // Check for objects
        if (typeof value === "object" && value.constructor === Object) {
            result = {};
            for (obj in value) {
                if (value.hasOwnProperty(obj)) {
                    result[obj] = tgfFunctions.copyValue(value[obj]);
                }
            }
            return result;
        }
        // Handle simple types
        return value;
    };
    // Return a merged version where value2 replaces elements of value1
    tgfFunctions.mergeValues = function (value1, value2) {
        "use strict";
        var i, result, obj;
        // Handle easy case
        if (value1 === undefined || value1 === null) {
            return tgfFunctions.copyValue(value2);
        }
        if (value2 === undefined || value2 === null) {
            return tgfFunctions.copyValue(value1);
        }
        // Handle arrays
        if (Object.prototype.toString.call(value1) === "[object Array]") {
            if (Object.prototype.toString.call(value2) !== "[object Array]") {
                return tgfFunctions.copyValue(value2);
            }
            result = tgfFunctions.copyValue(value1);
            for (i = 0; i < value2.length; i += 1) {
                result[i] = tgfFunctions.copyValue(value2[i]);
            }
            return result;
        }
        // Handle objects
        if (typeof value1 === "object" && value1.constructor === Object) {
            if (typeof value2 !== "object" || value2.constructor !== Object) {
                return tgfFunctions.copyValue(value2);
            }
            result = tgfFunctions.copyValue(value1);
            for (obj in value2) {
                if (value2.hasOwnProperty(obj)) {
                    result[obj] = tgfFunctions.copyValue(value2[obj]);
                }
            }
            return result;
        }
        // Handle simple types
        return tgfFunctions.copyValue(value2);
    };
    // ***************************************
    // Miscellaneous
    // ***************************************
    // Let C inherit the prototype of P, using the Holy Grail inheritance (JavaScript Patterns, page 127)
    tgfFunctions.inherit = function (C, P) {
        "use strict";
        var F = function () { };
        F.prototype = P.prototype;
        C.prototype = new F();
        C.uber = P.prototype;
        C.prototype.constructor = C;
    };
    // Generate a sort of unique id
    tgfFunctions.generateGuid = function () {
        "use strict";
        var guid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx";
        return guid.replace(/[xy]/g, function (c) {
            var r = defaultRandom.iRandomRange(0, 15);
            if (c === "x") {
                return r.toString(16);
            }
            if (c === "y") {
                return (r % 4 + 8).toString(16);
            }
        });
    };
    // Return the url parameters in a object of the form name: value
    tgfFunctions.getUrlParameters = function () {
        "use strict";
        var queryString = window.location.search.substring(1), params = {}, queries, temp, i;
        if (queryString) {
            // Split into key/value pairs
            queries = queryString.split("&");
            // Convert the array of strings into an object
            for (i = 0; i < queries.length; i += 1) {
                temp = queries[i].split("=");
                params[temp[0]] = temp[1];
            }
        }
        return params;
    };
    return tgfFunctions;
}());
export { tgfFunctions };
